import { useEffect, useRef, useState } from "react";
import { detectUploadMime, MAX_UPLOAD_BYTES } from "../../util/imageCompression.js";
import { type AllowedImageMime } from "../api.js";

export interface DraftImage {
  /** Stable client-side id used as the React key. */
  localId: string;
  file: File;
  contentType: AllowedImageMime;
  previewUrl: string;
}

export const MAX_DRAFT_IMAGES = 8;

/**
 * Holds the in-progress photo set as in-memory `File` blobs with object-URL
 * previews — nothing is uploaded here. The authenticated wizard uploads on
 * submit; the anonymous intake funnel holds them until the account exists, then
 * uploads. Object URLs are revoked on unmount and on remove so we don't leak.
 *
 * `addFiles` returns any per-file validation errors so the caller can surface
 * them in whatever error slot it already owns.
 */
export function useDraftImages() {
  const [images, setImages] = useState<DraftImage[]>([]);

  const imagesRef = useRef(images);
  imagesRef.current = images;
  useEffect(() => {
    return () => {
      for (const img of imagesRef.current) URL.revokeObjectURL(img.previewUrl);
    };
  }, []);

  function addFiles(fileList: FileList | null): string[] {
    if (!fileList || fileList.length === 0) return [];
    const next: DraftImage[] = [];
    const errors: string[] = [];
    const remaining = MAX_DRAFT_IMAGES - imagesRef.current.length;
    for (const file of Array.from(fileList).slice(0, remaining)) {
      const contentType = detectUploadMime(file);
      if (!contentType) {
        errors.push(`${file.name}: unsupported image type`);
        continue;
      }
      // Photos are downscaled at upload time, so this cap only has to catch
      // genuinely absurd input — not the ordinary 8-20 MB phone photo that
      // used to be rejected here.
      if (file.size > MAX_UPLOAD_BYTES) {
        errors.push(`${file.name} is too large (max 25 MB)`);
        continue;
      }
      next.push({
        localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        contentType,
        previewUrl: URL.createObjectURL(file),
      });
    }
    if (next.length > 0) setImages((prev) => [...prev, ...next]);
    return errors;
  }

  function removeImage(localId: string) {
    setImages((prev) => {
      const target = prev.find((p) => p.localId === localId);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.localId !== localId);
    });
  }

  return { images, addFiles, removeImage };
}

/**
 * A draft thumbnail that survives an unrenderable source. We accept HEIC (it's
 * what iPhones shoot) but Chrome and Firefox can't paint it, so the object URL
 * yields a broken image — fall back to a labeled tile so the user can still see
 * the photo is queued and can still remove it.
 */
function DraftThumb({ image }: { image: DraftImage }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded-md bg-slate-100 px-2 text-center text-[11px] leading-tight text-slate-500">
        <span className="line-clamp-3 break-all">{image.file.name}</span>
      </div>
    );
  }
  return (
    <img
      src={image.previewUrl}
      alt=""
      onError={() => setFailed(true)}
      className="aspect-square w-full rounded-md object-cover"
    />
  );
}

/**
 * Presentational photo step: existing previews + add/remove + a continue button.
 * State lives in the caller via `useDraftImages`.
 */
export function ImagesStep({
  images,
  onAdd,
  onRemove,
  onContinue,
}: {
  images: DraftImage[];
  onAdd: (files: FileList | null) => void;
  onRemove: (localId: string) => void;
  onContinue: () => void;
}) {
  const canAddMore = images.length < MAX_DRAFT_IMAGES;
  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">Add photos (optional)</h2>
      <p className="text-sm text-slate-500">
        Up to {MAX_DRAFT_IMAGES} photos. JPG, PNG, WebP, or HEIC — large photos are shrunk
        automatically.
      </p>

      {images.length > 0 && (
        <ul className="grid grid-cols-3 gap-2">
          {images.map((img) => (
            <li key={img.localId} className="relative">
              <DraftThumb image={img} />
              <button
                type="button"
                onClick={() => onRemove(img.localId)}
                aria-label="Remove photo"
                className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {canAddMore && (
        <label className="flex min-h-[44px] cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-white px-4 text-sm font-medium text-teal-700">
          {images.length === 0 ? "Take or choose photos" : "Add more"}
          <input
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(e) => {
              onAdd(e.target.files);
              // Reset so re-selecting the same file fires onChange again.
              e.target.value = "";
            }}
          />
        </label>
      )}

      <button
        type="button"
        onClick={onContinue}
        className="min-h-[44px] w-full rounded-md bg-teal-700 text-sm font-medium text-white"
      >
        {images.length === 0 ? "Skip photos" : "Continue"}
      </button>
    </div>
  );
}
